/**
 * NicePhone Mini External Integration
 * The browser softphone: JsSIP against NicePhone Mini / Asterisk.
 */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import axiosInstance from '../api/axiosInstance';
import API_ENDPOINTS from '../api/endpoints';

// The NicePhone vendor moved from phone.merawalaip.info/nicephone-mini-external
// to the phone.merawalaip.com root; scripts and the call-bar CSS live here.
const NICEPHONE_BASE = 'https://phone.merawalaip.com';
const NICEPHONE_SCRIPT_VERSION = '20260730e';

let scriptsLoaded = false;
let initPromise = null;

/** Only one SIP registration across duplicate Toolbar mounts (e.g. inline strips). */
let nicePhoneToolbarInitStarted = false;

const formBodyToObject = (body) => {
  const jsonBody = {};
  if (body instanceof FormData || body instanceof URLSearchParams) {
    body.forEach((value, key) => {
      jsonBody[key] = value;
    });
  }
  return jsonBody;
};

const getAuthToken = () => localStorage.getItem('token');

/**
 * call_control.php / call_metrics.php on phone.merawalaip.com have no CORS
 * headers for crm.nicedialtech.com. Route those fetches through our API.
 */
const installNicePhoneCorsProxy = () => {
  if (typeof window === 'undefined' || window.__nicePhoneCorsProxyInstalled) return;
  window.__nicePhoneCorsProxyInstalled = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = function (input, init = {}) {
    const url = typeof input === 'string' ? input : input?.url || '';
    let endpoint = null;
    if (url.includes('/call_control.php')) {
      endpoint = API_ENDPOINTS.nicephone.callControl;
    } else if (url.includes('/call_metrics.php')) {
      endpoint = API_ENDPOINTS.nicephone.callMetrics;
    }
    if (!endpoint) {
      return originalFetch(input, init);
    }

    const jsonBody = formBodyToObject(init.body);
    const headers = { 'Content-Type': 'application/json' };
    const token = getAuthToken();
    if (token) headers['x-api-key'] = token;

    return originalFetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(jsonBody),
    });
  };
};

/** JsSIP RTCSession status codes (see JsSIP C.STATUS_*). */
const JSSIP_STATUS_CANCELED = 7;
const JSSIP_STATUS_TERMINATED = 8;
const JSSIP_STATUS_CONFIRMED = 9;

/**
 * Vendor `_isSessionActive` treats `status >= 8` as ended, but an answered
 * JsSIP call sits at STATUS_CONFIRMED (9). The 2s incoming poll then calls
 * `_clearStaleSessions()` and drops the live session, so hold/mute fall back to
 * the AMI API and hangup never sends a SIP BYE. Only CANCELED/TERMINATED end a call.
 */
const isSipSessionActive = (sess) => {
  if (!sess) return false;
  try {
    if (typeof sess.isEnded === 'function') return !sess.isEnded();
    const status = sess.status;
    if (typeof status !== 'number') return true;
    return status !== JSSIP_STATUS_CANCELED && status !== JSSIP_STATUS_TERMINATED;
  } catch (e) {
    return false;
  }
};

/**
 * NicePhone's originate (call.php) returns ids like `call_69aa72d7725b6`, and
 * call_control.php / call_metrics.php key on that id. Asterisk uniqueids
 * (`1771894352.283`) come from the incoming poll and webhooks. Accept both.
 */
const looksLikeNicePhoneCallId = (id) => {
  const value = String(id || '').trim();
  return /^\d+\.\d+$/.test(value) || /^call_[a-z0-9]+$/i.test(value);
};

const isSipSessionEstablished = (sess) => {
  if (!sess) return false;
  try {
    if (typeof sess.isEstablished === 'function') return sess.isEstablished();
    return sess.status === JSSIP_STATUS_CONFIRMED;
  } catch (e) {
    return false;
  }
};

const patchNicePhoneCallControl = () => {
  const header = window.CRMCallHeader;
  if (!header || header.__lmsCallControlPatched) return;
  header.__lmsCallControlPatched = true;

  header._isSessionActive = isSipSessionActive;
  // Vendor only trusts `\d+.\d+`; without this the originate id is thrown away.
  header._looksLikeAstUniqueid = looksLikeNicePhoneCallId;

  /** Re-attach the live session if the vendor dropped the reference mid-call. */
  const restoreLiveSession = function () {
    if (this.session && isSipSessionActive(this.session)) return this.session;
    const candidates = [this.incomingSession, window.__incomingSipSession];
    for (let i = 0; i < candidates.length; i += 1) {
      if (candidates[i] && isSipSessionActive(candidates[i])) {
        this.session = candidates[i];
        return this.session;
      }
    }
    return null;
  };

  const vendorToggleMute = header.toggleMute;
  header.toggleMute = function () {
    restoreLiveSession.call(this);
    return vendorToggleMute.call(this);
  };

  const vendorToggleHold = header.toggleHold;
  header.toggleHold = function () {
    const sess = restoreLiveSession.call(this);
    // JsSIP hold() silently returns false unless the call is established.
    if (sess && !this.isOnHold && !isSipSessionEstablished(sess)) {
      console.warn('CRMCallHeader: hold ignored — call not established yet');
      return undefined;
    }
    const wasOnHold = !!this.isOnHold;
    const result = vendorToggleHold.call(this);
    // Browser re-INVITE hold can be refused by JsSIP (pending re-offer). Fall back
    // to NicePhone's AMI hold so the customer actually goes on hold.
    if (sess && typeof sess.isOnHold === 'function') {
      let sipHeld = false;
      try { sipHeld = !!sess.isOnHold().local; } catch (e) { sipHeld = false; }
      if (!wasOnHold && this.isOnHold && !sipHeld) {
        this.__lmsApiHold = true;
        this.callControlAPI('hold');
      } else if (wasOnHold && !this.isOnHold && this.__lmsApiHold) {
        this.__lmsApiHold = false;
        this.callControlAPI('unhold');
      }
    }
    return result;
  };

  const vendorHangup = header.hangup;
  header.hangup = function () {
    restoreLiveSession.call(this);
    return vendorHangup.call(this);
  };

  header.callControlAPI = async function (action, callidOverride) {
    // Prefer the NicePhone id (originate `call_…` or Asterisk uniqueid). Send whatever
    // we have; the backend proxy fills in the id from the active call record if needed.
    let callid = callidOverride != null && String(callidOverride).trim() !== ''
      ? String(callidOverride).trim()
      : (this._resolveAstCallId() || this.activeCallId || '');
    if (callid && !looksLikeNicePhoneCallId(callid)) {
      callid = this._resolveAstCallId() || callid;
    }
    if (!callid && !this.config.sipUser) return undefined;

    try {
      // Only the action and the call id are sent. sip_user and user_id used to be
      // sent from here and trusted by the backend for authorization; the backend
      // now derives both from the session and verifies call ownership itself.
      const response = await axiosInstance.post(API_ENDPOINTS.nicephone.callControl, {
        action,
        callid: callid || '',
      });
      return response.data;
    } catch (error) {
      console.error('CRMCallHeader: API call failed', error);
      return { ok: false, error: error.message };
    }
  };

  header.saveCallMetrics = async function () {
    const callid = this._resolveAstCallId() || this.activeCallId;
    if (!callid) return;
    try {
      await axiosInstance.post(API_ENDPOINTS.nicephone.callMetrics, {
        callid,
        hold_seconds: String(this.holdAccum || 0),
        mute_seconds: String(this.muteAccum || 0),
      });
    } catch (error) {
      console.error('CRMCallHeader: Failed to save metrics', error);
    }
  };
};

/**
 * Inject the call-bar stylesheet, once, and only from here.
 *
 * It used to be a static <link> in index.html, so every user requested it —
 * including superadmins and admins who never make a call — which is why a slow
 * or unreachable phone host dragged out their page load and logout. Injecting it
 * from the script loader (which only runs for users who can make calls) means
 * non-calling roles never touch the phone host at all.
 */
const loadNicePhoneStylesheet = () => {
  if (document.getElementById('nicephone-header-css')) return;
  const link = document.createElement('link');
  link.id = 'nicephone-header-css';
  link.rel = 'stylesheet';
  link.href = `${NICEPHONE_BASE}/crm-call-header.css`;
  document.head.appendChild(link);
};

const loadNicePhoneScripts = () => {
  if (scriptsLoaded) {
    return Promise.resolve();
  }

  if (initPromise) {
    return initPromise;
  }

  loadNicePhoneStylesheet();
  installNicePhoneCorsProxy();

  initPromise = new Promise((resolve, reject) => {
    if (document.getElementById('nicephone-jssip') &&
        document.getElementById('nicephone-header') &&
        document.getElementById('nicephone-secure')) {
      scriptsLoaded = true;
      patchNicePhoneCallControl();
      resolve();
      return;
    }

    const jssipScript = document.createElement('script');
    jssipScript.id = 'nicephone-jssip';
    jssipScript.src = `${NICEPHONE_BASE}/js/jssip.min.js`;
    jssipScript.async = true;
    jssipScript.onload = () => {
      const headerScript = document.createElement('script');
      headerScript.id = 'nicephone-header';
      headerScript.src = `${NICEPHONE_BASE}/crm-call-header.js?v=${NICEPHONE_SCRIPT_VERSION}`;
      headerScript.async = true;
      headerScript.onload = () => {
        const secureScript = document.createElement('script');
        secureScript.id = 'nicephone-secure';
        secureScript.src = `${NICEPHONE_BASE}/crm-call-header-secure.js?v=${NICEPHONE_SCRIPT_VERSION}`;
        secureScript.async = true;
        secureScript.onload = () => {
          scriptsLoaded = true;
          patchNicePhoneCallControl();
          resolve();
        };
        secureScript.onerror = () => {
          reject(new Error('Failed to load NicePhone secure script'));
        };
        document.body.appendChild(secureScript);
      };
      headerScript.onerror = () => {
        reject(new Error('Failed to load NicePhone header script'));
      };
      document.body.appendChild(headerScript);
    };
    jssipScript.onerror = () => {
      reject(new Error('Failed to load JSSIP script'));
    };
    document.body.appendChild(jssipScript);
  });

  return initPromise;
};

const initNicePhone = async ({ bootstrapToken, userId, credentialsEndpoint }) => {
  if (typeof window.CRMCallHeaderSecure === 'undefined') {
    await loadNicePhoneScripts();
    await new Promise((resolve) => setTimeout(resolve, 500));
  }

  if (typeof window.CRMCallHeaderSecure === 'undefined') {
    throw new Error('CRMCallHeaderSecure not available');
  }

  patchNicePhoneCallControl();

  try {
    await window.CRMCallHeaderSecure.init({
      // Points at our own backend rather than the vendor host, so the provider
      // API key never reaches the browser. The backend injects the real key and
      // resolves the advisor from the short-lived bootstrap token below.
      credentialsEndpoint,
      apiKey: bootstrapToken,
      userId,
      autoAnswerIncoming: false,
      showIncomingPanel: true,
      pollIncomingApi: true,
      notifyIncomingApi: true,
      emitBrowserEvents: true,
      onIncomingCall: (ev) => {
        if (window.__nicePhoneOutboundPending) {
          window.CRMCallHeaderSecure.answerIncoming();
          return;
        }
        console.log('NicePhone inbound', ev?.caller, ev?.callid);
      },
      onCallStart: (payload) => {
        setNicePhoneClickToCallMode(false);
        console.log('NicePhone connected', payload);
      },
      onCallEnd: (payload) => {
        setNicePhoneClickToCallMode(false);
        console.log('NicePhone ended', payload);
      },
    });
    return true;
  } catch (error) {
    console.error('NicePhone initialization error:', error);
    throw error;
  }
};

export const nicePhoneMakeCall = (phoneNumber) => {
  if (typeof window.CRMCallHeaderSecure === 'undefined') {
    console.error('NicePhone not initialized');
    toast.error('Phone system not ready. Please refresh the page.');
    return false;
  }

  try {
    const cleanNumber = phoneNumber.replace(/\D/g, '');
    window.CRMCallHeaderSecure.makeCall(cleanNumber);
    return true;
  } catch (error) {
    console.error('NicePhone makeCall error:', error);
    toast.error('Failed to initiate call');
    return false;
  }
};

export const isNicePhoneReady = () => {
  return typeof window.CRMCallHeaderSecure !== 'undefined' &&
         (window.CRMCallHeaderSecure.isReady?.() !== false ||
          typeof window.CRMCallHeaderSecure.makeCall === 'function');
};

/** Originate rings the agent's SIP phone first. Auto-answer that INVITE; keep the inbound modal for real inbound. */
let clickToCallResetTimer = null;
/**
 * Does the softphone currently hold a live SIP session?
 *
 * Mirrors the widget's own session resolution: the outgoing session, an incoming
 * one, or the globally stashed incoming session. Used to tell a genuinely live
 * call from a store that merely rehydrated as CALLING after a refresh.
 */
export const hasLiveSipSession = () => {
  const header = typeof window !== 'undefined' ? window.CRMCallHeader : null;
  if (!header) return false;
  const candidates = [header.session, header.incomingSession, window.__incomingSipSession];
  return candidates.some((sess) => isSipSessionActive(sess));
};

/** Has the vendor widget finished initialising? Judging a zombie before this races startup. */
export const isNicePhoneWidgetReady = () => {
  if (typeof window === 'undefined') return false;
  return !!window.CRMCallHeader && typeof window.CRMCallHeaderSecure !== 'undefined';
};

export const setNicePhoneClickToCallMode = (enabled) => {
  window.__nicePhoneOutboundPending = !!enabled;
  const header = window.CRMCallHeader;
  if (header?.config) {
    header.config.autoAnswerIncoming = !!enabled;
    header.config.showIncomingPanel = !enabled;
  }
  if (clickToCallResetTimer) {
    clearTimeout(clickToCallResetTimer);
    clickToCallResetTimer = null;
  }
  if (enabled) {
    clickToCallResetTimer = setTimeout(() => setNicePhoneClickToCallMode(false), 25000);
  }
};

const NicePhoneToolbar = () => {
  const { user } = useSelector((state) => state.auth);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.userIsAdmin) {
      console.log('NicePhoneToolbar: Not an advisor, skipping initialization');
      return;
    }

    if (nicePhoneToolbarInitStarted) return;
    nicePhoneToolbarInitStarted = true;

    /**
     * Identity comes from the backend, per advisor.
     *
     * This used to be a hardcoded `userId = 10` plus the production NicePhone
     * API key as a literal fallback — which put a permanent provider secret in
     * every bundle and registered every advisor as the same SIP identity.
     * Neither value exists in the frontend any more.
     */
    const initialize = async () => {
      try {
        const { data } = await axiosInstance.get(API_ENDPOINTS.nicephone.identity);
        const identity = data?.response?.[0] || data?.[0] || data;
        const userId = identity?.userId;
        const bootstrapToken = identity?.token;

        if (!userId || !bootstrapToken) {
          const message = 'No phone identity is configured for your account';
          setError(message);
          toast.error(message);
          return;
        }

        await initNicePhone({
          bootstrapToken,
          userId,
          credentialsEndpoint: API_ENDPOINTS.nicephone.credentials,
        });
        setIsInitialized(true);
      } catch (err) {
        const message = err.response?.data?.response?.[0]?.message
          || 'Failed to initialize phone system';
        console.error('NicePhone initialization failed:', message);
        setError(message);
        toast.error(message);
      }
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initialize);
    } else {
      initialize();
    }
  }, [user]);

  if (error && process.env.NODE_ENV !== 'production') {
    console.warn('NicePhoneToolbar:', error, isInitialized);
  }

  return null;
};

export default NicePhoneToolbar;
